import React, { Component } from "react";
import PropTypes from "prop-types";
import { line as d3Line } from "d3-shape";

import GenericChartComponent from "../GenericChartComponent";
import { getAxisCanvas, getMouseCanvas } from "../GenericComponent";

import {
  isDefined,
  getClosestItemIndexes,
  strokeDashTypes,
  getStrokeDasharray,
  hexToRGBA,
} from "../utils";

class LineSeries extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.isHover = this.isHover.bind(this);
  }
  isHover(moreProps) {
    // console.log("HERE")
    const { highlightOnHover, yAccessor, hoverTolerance } = this.props;

    if (!highlightOnHover) return false;

    const { mouseXY, currentItem, xScale, plotData } = moreProps;
    const {
      chartConfig: { yScale, origin },
    } = moreProps;

    const { xAccessor } = moreProps;

    let [x, y] = mouseXY;
    y += origin[1];
    const radius = hoverTolerance;

    const { left, right } = getClosestItemIndexes(
      plotData,
      xScale.invert(x),
      xAccessor,
    );
    if (left === right) {
      const cy = yScale(yAccessor(currentItem)) + origin[1];
      const cx = xScale(xAccessor(currentItem)) + origin[0];

      const hovering1 =
        Math.pow(x - cx, 2) + Math.pow(y - cy, 2) < Math.pow(radius, 2);

      return hovering1;
    } else {
      const l = plotData[left];
      const r = plotData[right];
      const x1 = xScale(xAccessor(l)) + origin[0];
      const y1 = yScale(yAccessor(l)) + origin[1];
      const x2 = xScale(xAccessor(r)) + origin[0];
      const y2 = yScale(yAccessor(r)) + origin[1];

      // y = m * x + b
      const m /* slope */ = (y2 - y1) / (x2 - x1);
      const b /* y intercept */ = -1 * m * x1 + y1;

      const desiredY = Math.round(m * x + b);

      const hovering2 = y >= desiredY - radius && y <= desiredY + radius;

      return hovering2;
    }
  }
  drawOnCanvas(ctx, moreProps) {
    const {
      stroke,
      yAccessor,
      defined,
      interpolation,
      canvasClip,
      // destructure color from stroke prop
    } = this.props;

    const { connectNulls } = this.props;

    const { xAccessor } = moreProps;
    const {
      xScale,
      chartConfig: { yScale },
      plotData,
      hovering,
    } = moreProps;

    if (canvasClip) {
      ctx.save();
      canvasClip(ctx, moreProps);
    }

    ctx.lineWidth = hovering ? (stroke.width + 1) * 1.5 : stroke.width;

    ctx.strokeStyle = hexToRGBA(stroke.color, stroke.opacity);
    ctx.setLineDash(getStrokeDasharray(stroke.dash).split(","));

    const dataSeries = d3Line()
      .x((d) => Math.round(xScale(xAccessor(d))))
      .y((d) => Math.round(yScale(yAccessor(d))));

    if (isDefined(interpolation)) {
      dataSeries.curve(interpolation);
    }
    if (!connectNulls) {
      dataSeries.defined((d) => defined(yAccessor(d)));
    }

    ctx.beginPath();
    dataSeries.context(ctx)(plotData);
    ctx.stroke();

    if (canvasClip) {
      ctx.restore();
    }
  }
  renderSVG(moreProps) {
    const { yAccessor, stroke, defined } = this.props;
    const { connectNulls } = this.props;
    const { interpolation, style } = this.props;
    const { xAccessor, chartConfig } = moreProps;

    const { xScale, plotData, hovering } = moreProps;

    const { yScale } = chartConfig;
    const dataSeries = d3Line()
      .x((d) => Math.round(xScale(xAccessor(d))))
      .y((d) => Math.round(yScale(yAccessor(d))));

    if (isDefined(interpolation)) {
      dataSeries.curve(interpolation);
    }
    if (!connectNulls) {
      dataSeries.defined((d) => defined(yAccessor(d)));
    }
    const d = dataSeries(plotData);

    const { className } = this.props;

    return (
      <path
        style={style}
        className={`${className} ${stroke.color ? "" : " line-stroke"}`}
        d={d}
        stroke={stroke.color}
        strokeOpacity={stroke.opacity}
        strokeWidth={hovering ? (stroke.width + 1) * 1.5 : stroke.width}
        strokeDasharray={getStrokeDasharray(stroke.dash)}
      />
    );
  }
  render() {
    const { highlightOnHover, onHover, onUnHover } = this.props;
    const hoverProps =
      highlightOnHover || onHover || onUnHover
        ? {
            isHover: this.isHover,
            drawOn: ["mousemove", "pan", "drag"],
            canvasToDraw: getMouseCanvas,
          }
        : {
            drawOn: ["pan", "drag"],
            canvasToDraw: getAxisCanvas,
          };

    return (
      <GenericChartComponent
        svgDraw={this.renderSVG}
        canvasDraw={this.drawOnCanvas}
        onClickWhenHover={this.props.onClick}
        onDoubleClickWhenHover={this.props.onDoubleClick}
        onContextMenuWhenHover={this.props.onContextMenu}
        onHover={this.props.onHover}
        onUnHover={this.props.onUnHover}
        {...hoverProps}
      />
    );
  }
}

/*
function segment(points, ctx) {
	ctx.beginPath();

	const [x, y] = first(points);
	ctx.moveTo(x, y);
	for (let i = 1; i < points.length; i++) {
		const [x1, y1] = points[i];
		ctx.lineTo(x1, y1);
	}

	ctx.stroke();
}
*/

LineSeries.propTypes = {
  className: PropTypes.string,
  defined: PropTypes.func,
  hoverTolerance: PropTypes.number,
  highlightOnHover: PropTypes.bool,
  onClick: PropTypes.func,
  onDoubleClick: PropTypes.func,
  onHover: PropTypes.func,
  onUnHover: PropTypes.func,
  onContextMenu: PropTypes.func,
  yAccessor: PropTypes.func,
  connectNulls: PropTypes.bool,
  interpolation: PropTypes.func,
  canvasClip: PropTypes.func,
  style: PropTypes.object,
  stroke: PropTypes.shape({
    color: PropTypes.string,
    opacity: PropTypes.number,
    width: PropTypes.number,
    dash: PropTypes.oneOf(strokeDashTypes),
  }),
};

LineSeries.defaultProps = {
  className: "line ",
  defined: (d) => !isNaN(d),
  hoverTolerance: 6,
  highlightOnHover: true,
  connectNulls: false,

  stroke: {
    color: "#4682B4",
    opacity: 1,
    width: 1,
    dash: "Solid",
  },
};

export default LineSeries;
